import { useEffect, useMemo, useState } from "react";
import Preloader from "../component/Preloader";
import Header from "../component/Header";
import LanguagePopup from "../component/LanguagePopup";
import Footer from "../component/Footer";
import FloatingWidgets from "../component/FloatingWidgets";
import HomeModals from "../component/HomeModals";
import useHomeInteractions from "../component/useHomeInteractions";
import { Link } from "../component/AppLink";
import { ArabicHeader, ArabicFooter } from "./ArabicHome";

const CART_STORAGE_KEY = "tarahumDonationCart";
const CHECKOUT_STORAGE_KEY = "tarahumCheckoutDraft";

const parseAmount = (value) => {
  const amount = Number(String(value || "").replace(/,/g, ""));
  return Number.isFinite(amount) ? amount : 0;
};

const formatAmount = (value) => parseAmount(value).toLocaleString("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const getQuantity = (item) => {
  const quantity = Number(item?.quantity);
  return Number.isFinite(quantity) && quantity > 0 ? Math.floor(quantity) : 1;
};

const getStoredCart = () => {
  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    const items = stored ? JSON.parse(stored) : [];
    return Array.isArray(items) ? items : [];
  } catch {
    return [];
  }
};

const copy = {
  en: {
    pageTitle: "Checkout",
    home: "Home",
    secureCheckout: "Secure Checkout",
    completeDonation: "Complete Your Donation",
    intro: "Enter your billing details and review your cart summary before making the final payment.",
    billingAddress: "Billing Address",
    billingHelp: "Please provide your correct contact and billing information.",
    firstName: "Enter First Name *",
    lastName: "Enter Last Name *",
    phone: "Enter Phone Number *",
    email: "Enter Email Address *",
    address: "Write your Address",
    secureNote: "Your information is used only to process your donation and contact you regarding this transaction.",
    yourCart: "Your Cart",
    summary: "Donation item summary",
    empty: "Your donation cart is empty.",
    browse: "Browse donation cases",
    quantity: "Quantity",
    subtotal: "Subtotal",
    shipping: "Shipping",
    free: "Free",
    total: "Total",
    paymentPartners: "Payment Method",
    card: "Credit / Debit Card",
    cardHelp: "Visa and MasterCard supported",
    payNow: "Pay Now",
  },
  ar: {
    pageTitle: "إتمام التبرع",
    home: "الرئيسية",
    secureCheckout: "دفع آمن",
    completeDonation: "أكمل تبرعك",
    intro: "أدخل بيانات الفوترة وراجع ملخص السلة قبل إتمام عملية الدفع.",
    billingAddress: "عنوان الفوترة",
    billingHelp: "يرجى تقديم معلومات الاتصال والفوترة الصحيحة.",
    firstName: "أدخل الاسم الأول *",
    lastName: "أدخل اسم العائلة *",
    phone: "أدخل رقم الهاتف *",
    email: "أدخل البريد الإلكتروني *",
    address: "اكتب العنوان",
    secureNote: "تُستخدم معلوماتك فقط لمعالجة التبرع والتواصل معك بشأن هذه العملية.",
    yourCart: "سلة التبرعات",
    summary: "ملخص عناصر التبرع",
    empty: "سلة التبرعات فارغة.",
    browse: "تصفح حالات التبرع",
    quantity: "الكمية",
    subtotal: "المجموع الفرعي",
    shipping: "الشحن",
    free: "مجاني",
    total: "الإجمالي",
    paymentPartners: "وسيلة الدفع",
    card: "بطاقة ائتمان / خصم",
    cardHelp: "تدعم فيزا وماستركارد",
    payNow: "ادفع الآن",
  },
};

export default function Checkout({ language = "en" }) {
  const interactions = useHomeInteractions();
  const text = copy[language] || copy.en;
  const [cartItems] = useState(() => getStoredCart().map((item) => ({
    ...item,
    quantity: getQuantity(item),
  })));
  const [billing, setBilling] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    address: "",
  });

  useEffect(() => {
    document.title = `${text.pageTitle} | Tarahum Charity Foundation`;
  }, [text.pageTitle]);

  useEffect(() => {
    if (language !== "ar") return undefined;
    document.documentElement.lang = "ar";
    document.documentElement.dir = "rtl";
    document.body.classList.add("arabic-layout-page");
    return () => {
      document.body.classList.remove("arabic-layout-page");
      document.documentElement.lang = "en";
      document.documentElement.dir = "ltr";
    };
  }, [language]);

  const subtotal = useMemo(
    () => cartItems.reduce((sum, item) => sum + (parseAmount(item.amount) * getQuantity(item)), 0),
    [cartItems]
  );

  const updateBilling = (event) => {
    const { name, value } = event.target;
    setBilling((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!cartItems.length || subtotal <= 0) return;

    const firstItem = cartItems[0] || {};
    const checkoutDraft = {
      billing,
      cartItems,
      subtotal,
      paymentMethod: "Credit / Debit Card",
      savedAt: new Date().toISOString(),
    };
    sessionStorage.setItem(CHECKOUT_STORAGE_KEY, JSON.stringify(checkoutDraft));

    interactions.onOpenDonationPopup({
      id: `cart-${Date.now()}`,
      catid: firstItem.catid || "0",
      subcatid: firstItem.subcatid || "0",
      projectid: firstItem.projectid || "0",
      title: language === "ar" ? "تبرعات السلة" : "Donation Cart",
      type: "cart",
      typeLabel: `${cartItems.length} ${language === "ar" ? "عنصر" : cartItems.length === 1 ? "item" : "items"}`,
      amount: String(subtotal),
      donorName: `${billing.firstName} ${billing.lastName}`.trim(),
      donorEmail: billing.email,
      donorPhone: billing.phone,
    });
  };

  return (
    <>
      <Preloader />
      {language === "ar" ? <ArabicHeader /> : <Header />}
      {language === "ar" ? null : <LanguagePopup />}

      <section className="kid-d-wrp pb-0">
        <div className="kid-container">
          <div className="kid-hero">
            <h1>{text.pageTitle}</h1>
            <ul className="kid-breadcrumb">
              <li><Link to={language === "ar" ? "/ar/" : "/"}>{text.home}</Link></li>
              <li>{"/"}</li>
              <li>{text.pageTitle}</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="checkout-wrap-1">
        <div className="container">
          <form className="checkout-container" onSubmit={handleSubmit}>
            <div className="checkout-head">
              <span>
                <i className="fa-solid fa-shield-heart"></i>
                {text.secureCheckout}
              </span>
              <h1>{text.completeDonation}</h1>
              <p>{text.intro}</p>
            </div>

            <div className="checkout-layout">
              <div className="billing-card">
                <div className="card-head">
                  <div className="card-icon"><i className="fa-solid fa-location-dot"></i></div>
                  <div>
                    <h2>{text.billingAddress}</h2>
                    <p>{text.billingHelp}</p>
                  </div>
                </div>

                <div className="billing-body contact-wrap">
                  <div className="billing-form contact-form">
                    <div className="contact-form-grid">
                      <div className="contact-field">
                        <input name="firstName" value={billing.firstName} onChange={updateBilling} type="text" placeholder={text.firstName} autoComplete="given-name" required />
                      </div>
                      <div className="contact-field">
                        <input name="lastName" value={billing.lastName} onChange={updateBilling} type="text" placeholder={text.lastName} autoComplete="family-name" required />
                      </div>
                      <div className="contact-field">
                        <input name="phone" value={billing.phone} onChange={updateBilling} type="tel" placeholder={text.phone} autoComplete="tel" required />
                      </div>
                      <div className="contact-field">
                        <input name="email" value={billing.email} onChange={updateBilling} type="email" placeholder={text.email} autoComplete="email" required />
                      </div>
                    </div>
                    <div className="contact-field">
                      <textarea name="address" value={billing.address} onChange={updateBilling} placeholder={text.address} autoComplete="street-address"></textarea>
                    </div>
                  </div>

                  <div className="secure-note">
                    <i className="fa-solid fa-lock"></i>
                    <p>{text.secureNote}</p>
                  </div>
                </div>
              </div>

              <aside className="checkout-side">
                <div className="cart-card">
                  <div className="card-head">
                    <div className="card-icon"><i className="fa-solid fa-cart-shopping"></i></div>
                    <div>
                      <h2>{text.yourCart}</h2>
                      <p>{text.summary}</p>
                    </div>
                  </div>

                  <div className="cart-body">
                    {cartItems.length ? cartItems.map((item) => (
                      <div className="cart-item" key={item.id}>
                        <div className="cart-item-info">
                          <div className="cart-item-top">
                            <h3>{item.title}</h3>
                            <strong>{formatAmount(parseAmount(item.amount) * getQuantity(item))} <span>{"AED"}</span></strong>
                          </div>
                          <p>
                            {item.typeLabel || ""}
                            {getQuantity(item) > 1 ? ` · ${text.quantity}: ${getQuantity(item)}` : ""}
                          </p>
                        </div>
                      </div>
                    )) : (
                      <div className="cart-item">
                        <div className="cart-item-info">
                          <h3>{text.empty}</h3>
                          <p><Link to={language === "ar" ? "/ar/critical-cases" : "/critical-cases"}>{text.browse}</Link></p>
                        </div>
                      </div>
                    )}

                    <div className="price-lines">
                      <div className="price-line">
                        <span>{text.subtotal}</span>
                        <strong>{formatAmount(subtotal)}{" AED"}</strong>
                      </div>
                      <div className="price-line">
                        <span>{text.shipping}</span>
                        <strong className="free">{text.free}</strong>
                      </div>
                    </div>

                    <div className="total-box">
                      <span>{text.total}</span>
                      <strong>{formatAmount(subtotal)} <small>{"AED"}</small></strong>
                    </div>
                  </div>
                </div>

                <div className="payment-card">
                  <h3><i className="fa-solid fa-credit-card"></i>{text.paymentPartners}</h3>
                  <div className="paynt-opt-slct-chckout">
                    <label className="payment-option active">
                      <input type="radio" name="payment" value="Credit / Debit Card" defaultChecked />
                      <div className="payment-card">
                        <div className="payment-left">
                          <img src="/images/img/credit-card.png" alt="" />
                          <div className="payment-text">
                            <h4>{text.card}</h4>
                            <p>{text.cardHelp}</p>
                          </div>
                        </div>
                        <div className="check-mark"><i className="fa-solid fa-check"></i></div>
                      </div>
                    </label>
                  </div>

                  <button type="submit" className="pay-btn" disabled={!cartItems.length || subtotal <= 0}>
                    {text.payNow}
                    <i className={`fa-solid fa-arrow-${language === "ar" ? "left" : "right"}`}></i>
                  </button>
                </div>
              </aside>
            </div>
          </form>
        </div>
      </section>

      {language === "ar" ? <ArabicFooter /> : <Footer />}
      <FloatingWidgets {...interactions} />
      <HomeModals {...interactions} />
    </>
  );
}
